import React from 'react';

import { RootStateTypes, useTypedAppSelector } from '@src/redux/store/store.types';

import useGsapLoaded from '@src/hooks/useGsapLoaded';

import isEmpty from '@src/utils/isEmpty';
import scrollTo from '@src/utils/scrollTo';

const useAnimateFeaturedPost = ({ categorySlug }: { categorySlug: string | undefined }) => {
  const isGsapLoaded = useGsapLoaded();

  const { latestNewsData } = useTypedAppSelector((state: RootStateTypes) => state.newsRXS);

  const featureImageRef = React.useRef<HTMLDivElement>(null);
  const featuredPostTL = React.useRef<any>(null); // Adjusted to GSAPTimeline type or null

  React.useEffect(() => {
    if (isGsapLoaded && featureImageRef.current && !isEmpty({ value: latestNewsData })) {
      // Only setup the timeline if GSAP is loaded, featureImageRef.current is available, and latestNewsData is not empty
      featuredPostTL.current = window.gsap.timeline({ paused: true });

      featuredPostTL.current
        .to(
          featureImageRef.current,
          {
            duration: 0.5,
            ease: 'power4.inOut',
            css: { opacity: 0 },
            immediateRender: false,
          },
          'start'
        )
        .to(
          featureImageRef.current,
          {
            delay: 0.1,
            ease: 'power4.inOut',
            duration: 0.7,
            css: {
              height: '0',
            },
            immediateRender: false,
          },
          'start'
        )
        .to(featureImageRef.current, {
          duration: 0.7,
          ease: 'power4.inOut',
          y: () => featureImageRef.current?.offsetHeight || 0, // Ensures the use of the latest height
          immediateRender: false,
        });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGsapLoaded, latestNewsData, featureImageRef.current]);

  React.useEffect(() => {
    if (!isEmpty({ value: categorySlug })) {
      featuredPostTL.current?.play();
      scrollTo({ target: 'body', duration: 0.7 })();
    } else {
      featuredPostTL.current?.reverse();
    }
  }, [categorySlug]);

  return { featureImageRef };
};

export default useAnimateFeaturedPost;
